import type { AsyncData } from '#app';
import type {
  IModelResponse,
  ModelResponse,
} from '~/services/base/dto/base.dto';
import type {
  IModelConversationDetailResponse,
  ModelPOSTConversationAssociationConversationFile,
} from './dto/conversation.detail.dto';
import type {
  IModelDepartmentResponse,
  IModelGETConversationAssociationConversationArchivedRequest,
  IModelGETConversationAssociationConversationArchivedResponse,
  IModelGETConversationAssociationConversationRequest,
  IModelGETConversationAssociationConversationResponse,
  IModelGETConversationFeedbackDataResponse,
  IModelGETConversationFeedbackResponse,
  IModelOptionResponse,
  IModelPUTConversationRequest,
} from './dto/conversation.dto';

export class ConversationRepository {
  private api: any;
  private configService: any;
  private llmService: any;

  constructor(api: any, configService: any) {
    const runtimeConfig = useRuntimeConfig();
    this.api = api;
    this.configService = configService;
    this.llmService = runtimeConfig.public.apiLLMService;
  }

  async getConversationAssociationConversation(
    params: IModelGETConversationAssociationConversationRequest
  ): Promise<
    AsyncData<IModelGETConversationAssociationConversationResponse, any>
  > {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation`,
      this.configService,
      {
        params,
      }
    );
    return dataResult;
  }

  async getConversationAssociationConversationArchived(
    params: IModelGETConversationAssociationConversationArchivedRequest
  ): Promise<
    AsyncData<IModelGETConversationAssociationConversationArchivedResponse, any>
  > {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation-archived`,
      this.configService,
      {
        params,
      }
    );
    return dataResult;
  }

  async putConversation(
    params: IModelPUTConversationRequest
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation`,
      this.configService,
      {
        method: 'PUT',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async getConversationDetail(
    id: string | number
  ): Promise<AsyncData<IModelResponse<IModelConversationDetailResponse>, any>> {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation-message/${id}`,
      this.configService
    );
    return dataResult;
  }

  async createConversation(params: any): Promise<AsyncData<any, any>> {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation`,
      this.configService,
      {
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async initialConversation(params: any): Promise<AsyncData<any, any>> {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation/${params.id}/initial-llm`,
      this.configService,
      {
        method: 'PUT',
      }
    );
    return dataResult;
  }

  async appendConversation(params: any): Promise<AsyncData<any, any>> {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation/${params.id}/append-llm`,
      this.configService,
      {
        method: 'PUT',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async conversationPreview(params: any, signal?: AbortSignal): Promise<Response> {
    const response = await useApiFetchSSE(
      `${this.llmService}/v1/conversation-association/conversation/preview`,
      {
        method: 'POST',
        body: JSON.stringify({
          data: params,
        }),
        signal,
      }
    );
    return response;
  }

  async conversationAssociationConversationFile(
    params: any
  ): Promise<
    AsyncData<
      ModelResponse<ModelPOSTConversationAssociationConversationFile[]>,
      any
    >
  > {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/conversation-file`,
      this.configService,
      {
        method: 'POST',
        body: params,
      }
    );
    return dataResult;
  }

  async getModel(): Promise<
    AsyncData<IModelResponse<IModelOptionResponse[]>, any>
  > {
    const dataResult = await this.api(
      `${this.llmService}/v1/model`,
      this.configService
    );
    return dataResult;
  }

  async getDepartment(): Promise<
    AsyncData<IModelResponse<IModelDepartmentResponse[]>, any>
  > {
    const dataResult = await this.api(
      `${this.llmService}/v1/department`,
      this.configService
    );
    return dataResult;
  }

  async getConversationAssociationConversationFeedback(params: any): Promise<AsyncData<IModelGETConversationFeedbackResponse, any>> {
    const dataResult = await this.api(
      `${this.llmService}/v1/conversation-association/message-thumbs-down`,
      this.configService,
      {
        params,
      }
    );
    return dataResult;
  }
}
